// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.58.0 (Slice C, AC-C1–AC-C5) — the framework summary export and the
 * briefing record on a framework.
 *
 * ## The briefing record
 *
 * A small, generic, append-only record on ANY installed framework: a briefing
 * the organisation held about it, or a consultation comment the organisation
 * sent on it (UC-VN-114 steps 3 and 4). Both kinds are properties of the
 * activity, not of any instrument (sprint rule 5, R-46): no framework code,
 * instrument number or authority is named here, and nothing branches on the
 * framework the record is about.
 *
 * - The server stamps `framework_code`, `framework_status` and `pack_version`
 *   at the time of recording, so a later pack upgrade can say *briefed on pack
 *   version {v}; this framework has changed since* (AC-C4) without rewriting
 *   the entry.
 * - Append-only: no route edits or deletes an entry. A correction is a new
 *   entry naming `correction_of` with a required `correction_note`; the
 *   corrected entry stays, and the read model names the entry that corrects it
 *   (`corrected_by`) — the 62.57.0 communication-record shape (REUSE §54).
 * - The named approver approves once (`approved_at`, `approved_by`); a second
 *   approval answers 409 and anyone else 403.
 * - A `consultation_comment` is the ORGANISATION's own act (AC-C5): it records
 *   what the organisation sent, to whom and when. ComplianceOne submits
 *   nothing to any authority, and nothing here models a submission.
 * - Documents bind after create through the generic attachments with owner
 *   type `framework_briefing` (owner the entry), including the summary export
 *   artefact (AC-C1), which the export dialog offers to attach.
 */
/** Where the entries live. Every document carries `org_id` first (data model). */
export const FRAMEWORK_BRIEFINGS_COLLECTION = 'grc_framework_briefings';
/** What the entry records — a property of the activity (sprint rule 5). */
export const FRAMEWORK_BRIEFING_KINDS = ['briefing', 'consultation_comment'];
/** How it was held or sent — closed and instrument-neutral. */
export const FRAMEWORK_BRIEFING_CHANNELS = [
    'meeting',
    'email',
    'document',
    'webinar',
    'other',
];
/** The bounds AC-C3 states, and the ones the body schema enforces. */
export const FRAMEWORK_BRIEFING_LIMITS = {
    audience: 300,
    summary: 4000,
    channel_note: 300,
    reference: 200,
    correction_note: 2000,
};
/** The error codes the briefing record answers with. */
export const FRAMEWORK_BRIEFING_ERRORS = {
    notFound: 'FRAMEWORK_BRIEFING_NOT_FOUND',
    alreadyApproved: 'FRAMEWORK_BRIEFING_ALREADY_APPROVED',
    notApprover: 'FRAMEWORK_BRIEFING_NOT_APPROVER',
    heldOnInvalid: 'FRAMEWORK_BRIEFING_HELD_ON_INVALID',
    approverNotFound: 'FRAMEWORK_BRIEFING_APPROVER_NOT_FOUND',
    /**
     * Gate E12.5 pass 1, P3-8 (orchestrator decision) — the approver is a
     * different person from the recorder: naming yourself as the approver is
     * refused (400), and so is approving an entry you recorded (403).
     */
    selfApproval: 'FRAMEWORK_BRIEFING_SELF_APPROVAL',
};
/** AC-C1 — the formats the framework summary export renders. */
export const FRAMEWORK_SUMMARY_EXPORT_FORMATS = ['pdf', 'docx', 'xlsx'];
//# sourceMappingURL=framework-briefings.js.map